'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface WeldingStep {
  id: number;
  title: string;
  description: string;
  image: string;
  alt: string;
  details: string[];
}

const weldingSteps: WeldingStep[] = [
  {
    id: 1,
    title: 'Material Preparation',
    description: 'Precision cleaning and surface preparation in contamination-controlled environment to ensure weld integrity.',
    image: "https://xdfjtjgomqbjrrhygnbj.supabase.co/storage/v1/object/public/welding-processes/material-preparation.jpg",
    alt: 'Technician cleaning and preparing metal surfaces for welding in contamination-controlled cleanroom environment',
    details: [
      'Surface Decontamination',
      'Joint Fit-Up Verification',
      'Material Identification',
      'Pre-Weld Inspection'
    ]
  },
  {
    id: 2,
    title: 'Welding Execution',
    description: 'Certified welders perform precision welding using GTAW, GMAW, FCAW, or SMAW processes with real-time monitoring.',
    image: "https://xdfjtjgomqbjrrhygnbj.supabase.co/storage/v1/object/public/welding-processes/welding-execution.jpg",
    alt: 'Certified welder performing precision TIG welding with bright blue arc on stainless steel component',
    details: [
      'Certified Welders',
      'Process Control',
      'Heat Input Monitoring',
      'Shielding Gas Management'
    ]
  },
  {
    id: 3,
    title: 'Quality Verification',
    description: 'Non-destructive testing and visual inspection to verify weld quality, penetration, and structural integrity.',
    image: "https://xdfjtjgomqbjrrhygnbj.supabase.co/storage/v1/object/public/welding-processes/quality-verification.jpg",
    alt: 'Quality inspector examining welded joint with magnification equipment and NDT testing tools',
    details: [
      'Visual Inspection',
      'NDT Testing',
      'Dimensional Verification',
      'Weld Profile Analysis'
    ]
  },
  {
    id: 4,
    title: 'Documentation & Traceability',
    description: 'Complete weld documentation with welder certification, procedure qualification, and full material traceability.',
    image: "https://xdfjtjgomqbjrrhygnbj.supabase.co/storage/v1/object/public/welding-processes/documentation-traceability.jpg",
    alt: 'Quality documentation system showing weld procedure specifications and certification records on digital tablet',
    details: [
      'Welder Certification Records',
      'WPS/PQR Documentation',
      'Material Traceability',
      'Audit-Ready Reports'
    ]
  }
];

export default function WeldingProcessShowcase() {
  const [activeStep, setActiveStep] = useState(1);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleStepClick = (id: number) => {
    if (!isHydrated) return;
    setActiveStep(id);
  };

  const currentStep = weldingSteps.find((step) => step.id === activeStep) || weldingSteps[0];

  return (
    <section className="lg:py-24 bg-background py-[70px]">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="FireIcon" size={20} variant="solid" className="text-primary" />
            <span className="text-sm font-cta font-semibold text-primary">Process Excellence</span>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-headline font-bold text-foreground mb-6">
            Comprehensive Welding Processes
          </h2>
          
          <p className="text-lg text-muted-foreground leading-relaxed">
            Our systematic welding process ensures contamination control, quality verification, and full traceability from preparation to final documentation.
          </p>
        </div>
        
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {weldingSteps.map((step) => (
              <button
                key={step.id}
                onClick={() => handleStepClick(step.id)}
                className={`p-4 rounded-lg border-2 transition-all duration-base text-left ${
                  activeStep === step.id
                    ? 'border-primary bg-primary/5' :'border-border bg-card hover:border-primary/50'
                }`}
              >
                <div className="flex items-center space-x-3 mb-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-headline font-bold text-sm ${
                      activeStep === step.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {step.id}
                  </div>
                  <h3
                    className={`font-headline font-bold text-sm lg:text-base ${
                      activeStep === step.id ? 'text-primary' : 'text-foreground'
                    }`}
                  >
                    {step.title}
                  </h3>
                </div>
              </button>
            ))}
          </div>
          
          <div className="bg-card rounded-xl shadow-lg overflow-hidden border border-border">
            <div className="grid grid-cols-1 lg:grid-cols-2">
              <div className="relative h-64 lg:h-auto">
                <AppImage
                  key={currentStep.id}
                  src={currentStep.image}
                  alt={currentStep.alt}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="p-8 lg:p-12">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                    <span className="text-2xl font-headline font-bold text-primary-foreground">
                      {currentStep.id}
                    </span>
                  </div>
                  <h3 className="text-2xl font-headline font-bold text-foreground">
                    {currentStep.title}
                  </h3>
                </div>
                
                <p className="text-base text-muted-foreground mb-6 leading-relaxed">
                  {currentStep.description}
                </p>
                
                <div className="space-y-3">
                  <h4 className="text-sm font-cta font-semibold text-foreground uppercase tracking-wide">
                    Key Activities
                  </h4>
                  <ul className="space-y-2">
                    {currentStep.details.map((detail, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <Icon
                          name="CheckCircleIcon"
                          size={20}
                          variant="solid"
                          className="text-primary flex-shrink-0 mt-0.5"
                        />
                        <span className="text-sm text-muted-foreground">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}